import { api } from '@/lib/api.js';
import { inFlightDedup } from '@/lib/inFlight.js';

// Submission Builder (Slice 4b) — thin client over the 4a authoring routes
// in routes/tracon.routes.js. Reads are inFlight-deduped; writes always hit
// the network (backend invalidates tracon_deal_${dealId} + tracon_pipeline
// on every write, so the next read is fresh).

// Drop a carrier PDF onto a market row → upload + file it to that quote (L.4
// attach half; not auto-parse). Multipart bypasses the JSON api wrapper
// (PolicyDocsDropSheet / uploadLicensePdf pattern); the browser sets the
// boundary. quoteId is the Supabase quotes-row id; the server links it via
// inbound_documents.matched_quote_id and skips the AI classifier.
export async function dropQuotePdf(dealId, quoteId, file) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('deal_id', String(dealId));
  if (quoteId) fd.append('quote_id', String(quoteId));
  const res = await fetch('/api/tracon/drop-pdf', {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: fd,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) throw new Error(body.error || `upload failed (HTTP ${res.status})`);
  return body;
}

export function listSubmissionsForDeal(dealId) {
  return inFlightDedup(`submissions:${dealId}`, () =>
    api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/submissions`)
  );
}

// All classified docs filed to the deal (every type) — the distribute on-file
// checklist matches expected docs to a classified document_type instead of
// best-effort filename regex.
export function listPolicyDocs(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/policy-docs`);
}

// Create a submission for the deal — backend seeds roster (rated pilots with
// on-file PHFs, named-excluded, aircraft) + one "Current terms" scenario.
export function createSubmission(dealId, body = {}) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/submissions`, body);
}

// The matrix feed: scenarios (label + coverage + aircraft_hull + is_selected)
// each carrying their quotes rows. Cell premium = quotes.total_premium —
// NEVER a per-cell CRM fetch (4b.0).
export function getSubmissionQuotations(submissionId) {
  return inFlightDedup(`submission-quotations:${submissionId}`, () =>
    api.get(`/api/tracon/submissions/${encodeURIComponent(submissionId)}/quotations`)
  );
}

export function createScenario(submissionId, body = {}) {
  return api.post(`/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios`, body);
}

export function cloneScenario(submissionId, scenarioId, body = {}) {
  return api.post(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/clone`,
    body
  );
}

export function deleteScenario(submissionId, scenarioId) {
  return api.del(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}`
  );
}

// Single-selected invariant lives server-side (siblings de-selected).
export function selectScenario(submissionId, scenarioId, isSelected = true) {
  return api.put(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/select`,
    { is_selected: isSelected }
  );
}

// Roster membership writes submissions.pilots/aircraft ONLY — never
// Approved_Pilots (that subform is the OUTPUT of the bound submission).
export function patchRoster(submissionId, body) {
  return api.patch(`/api/tracon/submissions/${encodeURIComponent(submissionId)}/roster`, body);
}

// Explicit carrier fan-out — materializing creates real CRM Quotations.
export function materializeScenario(submissionId, scenarioId, carriers, effectiveDate = null) {
  return api.post(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/materialize`,
    { carriers, effective_date: effectiveDate }
  );
}

// Fire the live IAT AeroQuote pipeline for a deal: builds the deal's manifest
// into IAT XML, submits via iatService.submitQuote, and parses the rate.
// Returns { success, quote: { quoteNumber, premiums: { hull, liability, total,
// ... }, errors, ... } }. Needs a session + manifest on the deal (404/400 if
// absent). The cockpit then lands the parsed rate via createScenarioQuote.
export function runIatQuote(dealId) {
  return api.post(`/api/tracon/iat-quote/${encodeURIComponent(dealId)}`, {});
}

// Fire the live Global Aerospace SNK pipeline for a deal: builds the deal's
// manifest into Global's quote request, POSTs api/v1/quote/snk, parses the
// AircraftQuoteResponse, and persists it (source 'global_snk').
//
// Returns { success, quote, mapper_warnings, quote_id } where quote carries:
//   hasTerms      — the ONLY safe gate for landing a priced cell. A referred or
//                   declined response still returns a quote number and a full
//                   hull/liability option ladder, but no premium; treating that
//                   as a quote lands a $0 row that drags the comparison down.
//   isReferred / isDeclined / referDeclineReasons
//   premiums      — { total, surcharge, amount, hull, liability, medical }
//   warnings      — carrier-side notices (medical limit dropped, etc.)
// mapper_warnings are OUR notices raised while building the request (coverage
// date clamped, pilot/unit experience unmatched, entity insured unconfirmed).
// Both must reach a human — see the SubmissionBuilderPanel Global branch.
export function runGlobalQuote(dealId) {
  // 120s, not the 30s default. Global's rater takes markedly longer on a
  // multi-aircraft submission, and aborting client-side turns a real answer
  // ("refers to underwriter review", the expected outcome for a fleet) into a
  // meaningless timeout. The server waits 90s; this has to outlast it.
  return api.post('/api/global/quote', { deal_id: dealId }, { timeoutMs: 120_000 });
}

// Quote-readiness gate (DISPATCH-PREFLIGHT-SPEC). Pure dry-run — the server
// builds the request the dispatch would send and classifies what will fail or
// mis-rate, WITHOUT calling the carrier. Returns:
//   { carrier, ready, blockers: [{code, severity, scope, ref, message,
//     remedy_link}], warnings: [{code, message}], preflight_supported }
// `ready` is false only when blockers is non-empty. A carrier that has not
// adopted preflight answers ready:true + preflight_supported:false, so the gate
// stays out of the way rather than inventing a readiness opinion.
// Price a broker's option-ladder selection. Pure server-side computation over
// the PERSISTED Global quote (no carrier call): returns { totals, landing_body }
// where landing_body is the createScenarioQuote payload for the chosen basis.
// 400s when the selection is incomplete or the multi-aircraft ladder is
// ambiguous — nothing falls through to Global's default basis.
export function landGlobalSelection(quoteId, selection) {
  return api.post('/api/global/quote/land', { quote_id: quoteId, selection });
}

export function getCarrierReadiness(dealId, carrierSlug) {
  return api.get(
    `/api/shop/readiness/${encodeURIComponent(dealId)}?carrier=${encodeURIComponent(carrierSlug)}`
  );
}

// 4b-2 cell editor: manual quote on a (scenario × carrier) cell. The route
// creates the CRM Quotation (scenario supplies coverage defaults, body
// supplies the carrier's rate + any overrides) and stamps
// quotes.total_premium. It owns the CRM-write traps — AoQ row-id
// preservation, toCRMDateOnly, Tail_Number {id} lookup.
export function createScenarioQuote(submissionId, scenarioId, body) {
  return api.post(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/quotes`,
    body
  );
}

// PATCH a manual quote — quoteId is the Supabase quotes-row id (not the CRM
// Quotation id). Subform rows must carry their `id` (the subform-dup rule).
export function patchScenarioQuote(submissionId, scenarioId, quoteId, body) {
  return api.patch(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/quotes/${encodeURIComponent(quoteId)}`,
    body
  );
}

// Slice T cockpit: per-quote shopping fields (status lane, Subject-to/Referred
// flags, presenting toggle, channel override). Supabase-only — never touches
// the CRM-mirrored quote status or the quote-save path.
export function patchQuoteShopping(submissionId, scenarioId, quoteId, body) {
  return api.patch(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/quotes/${encodeURIComponent(quoteId)}/shopping`,
    body
  );
}

// Slice 5b: bind a CELL — one (scenario × carrier) Quotation becomes the
// policy. quoteId is the Supabase quotes-row id. The 5a route rewrites
// Approved_Pilots to the rated roster, syncs named exclusions to
// sessions.excluded_pilots, and flips the Quotation to Bound; the broker
// then continues in the EXISTING confirm-binding wizard (tower URL) — or,
// for a no-app renewal bind (opts.no_new_application, FLOW 2026-07-11),
// skips the wizard and chains into the renewal payment request.
export function bindScenarioQuote(submissionId, scenarioId, quoteId, opts = {}) {
  return api.post(
    `/api/tracon/submissions/${encodeURIComponent(submissionId)}/scenarios/${encodeURIComponent(scenarioId)}/quotes/${encodeURIComponent(quoteId)}/bind`,
    opts
  );
}

// Carrier registry (slug / name / routing_type / active) — drives which
// cells offer "Materialize" (routing_type 'api') vs "Add quote" (manual),
// and the "Add carrier" column picker.
export function listCarrierRegistry() {
  return inFlightDedup('sb-carrier-registry', () => api.get('/api/tracon/admin/carriers'));
}

// Land a Global ladder pick onto the cockpit's Markets surface
// (GLOBAL-COCKPIT-QUOTE-WIRING-SPEC). Same server-side re-price as
// landGlobalSelection — the premium is rebuilt from the persisted quote, never
// from anything the client computed — but it also corrects the captured row off
// Global's default basis and flips the carrier's market row to quoted, so the
// deal stops reading "No submissions logged yet".
//
// landGlobalSelection stays for the Submission Builder's scenario grid, which
// lands into a (scenario x carrier) cell instead of a market row.
// Record a terminal, NON-PRICED Global outcome (referred or declined) on the
// deal's market row. Same door as the priced landing, deliberately: an outcome
// the broker has to act on belongs next to the other markets, not in a toast
// that scrolls away. Never carries a premium, because Global returns none.
export function recordGlobalOutcome(dealId, { outcome, reasons, warnings, quoteId }) {
  return api.post('/api/global/quote/land-to-market',
    { deal_id: dealId, outcome, reasons, warnings, quote_id: quoteId || null }, { timeoutMs: 60_000 });
}

// `alternates` are the richer rungs the broker tagged "also present". They land
// as OPTIONAL coverage parts on the quote, which is what the binding wizard
// reads, so the client elects the upgrade rather than the broker choosing it.
export function landGlobalToMarket(dealId, quoteId, selection, alternates) {
  return api.post('/api/global/quote/land-to-market',
    { deal_id: dealId, quote_id: quoteId, selection, alternates }, { timeoutMs: 60_000 });
}

// ── Live-API carriers: is the door real? ────────────────────────────────────
//
// Asked of PRODUCTION 2026-08-05:
//   IAT     → environment 'prod', https://iatflightapi.com, credentials set
//   Global  → environment 'test', https://uatexternalservices.global-aero.com
// So "Quote with Global" has been firing at Global's UAT from production: a
// control that looks live and cannot produce a real quote.
//
// Deliberately NOT /api/iat/status or /api/global/status. Both sit behind
// authenticateApiKey and answer a bare broker JWT with "Invalid API key", so a
// browser probing them reads NOT-ready and the button silently never renders.
// /api/tracon/api-carriers is the same fact inside the JWT-authed namespace
// this app already uses. The server still reads each integration's own config,
// so configuring production flips the button on with no code change.
export function getApiCarriers() {
  return api.get('/api/tracon/api-carriers');
}
